/**
 * Shared media handling for the four Xamseen media types: photos, videos,
 * audio and documents.
 *
 * Videos are stored in the private `secure-video` bucket and are never served
 * from a guessable public URL — playback goes through a short-lived signed link
 * issued by `resolveMediaPlayback`, which re-checks that the parent report or
 * project is actually visible to the caller.
 *
 * Photos, audio and documents keep using the existing public `post-media`
 * bucket so nothing that already works changes.
 */

import { supabase } from "@/integrations/supabase/client";

export type MediaKind = "image" | "video" | "audio" | "document";

export const VIDEO_BUCKET = "secure-video";
export const PUBLIC_MEDIA_BUCKET = "post-media";

export const MEDIA_LIMITS = {
  image: { bytes: 8 * 1024 * 1024, label: "8MB" },
  video: { bytes: 200 * 1024 * 1024, label: "200MB", maxSeconds: 300 },
  audio: { bytes: 25 * 1024 * 1024, label: "25MB", maxSeconds: 900 },
  document: { bytes: 20 * 1024 * 1024, label: "20MB" },
} as const;

export const VIDEO_ACCEPT = "video/mp4,video/quicktime,video/webm,video/x-m4v,video/*";
export const AUDIO_ACCEPT = "audio/mpeg,audio/mp4,audio/wav,audio/webm,audio/*";
export const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt";

/** Human, direct copy for the composer and error states. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds)) return "";
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ------------------------------ local inspection ------------------------------ */

export type VideoProbe = { duration: number; width: number; height: number; posterBlob: Blob | null };

/**
 * Reads duration and dimensions from the chosen file and grabs a frame for the
 * poster. Everything happens locally in the browser — no device metadata beyond
 * the frame itself ever leaves the device.
 */
export function probeVideo(file: File): Promise<VideoProbe> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    const fail = () => { URL.revokeObjectURL(url); reject(new Error("That video could not be read on this device.")); };
    video.onerror = fail;

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const seekTo = Math.min(duration > 0 ? duration * 0.1 : 0, 2);
      const finish = (posterBlob: Blob | null) => {
        URL.revokeObjectURL(url);
        resolve({ duration, width: video.videoWidth, height: video.videoHeight, posterBlob });
      };
      const capture = () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = Math.min(1, 1280 / Math.max(1, video.videoWidth));
          canvas.width = Math.max(2, Math.round(video.videoWidth * scale));
          canvas.height = Math.max(2, Math.round(video.videoHeight * scale));
          const ctx = canvas.getContext("2d");
          if (!ctx) return finish(null);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => finish(b), "image/jpeg", 0.72);
        } catch {
          finish(null);
        }
      };
      video.onseeked = capture;
      try { video.currentTime = seekTo; } catch { capture(); }
      // Some browsers never fire onseeked for tiny clips.
      window.setTimeout(() => { if (video.readyState >= 2) capture(); }, 1500);
    };
  });
}

export function probeAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("audio");
    el.preload = "metadata";
    el.src = url;
    el.onloadedmetadata = () => { const d = el.duration; URL.revokeObjectURL(url); resolve(Number.isFinite(d) ? d : 0); };
    el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
  });
}

/** Returns an error message, or null when the file is acceptable. */
export function validateMediaFile(file: File, kind: MediaKind, durationSeconds?: number): string | null {
  const limit = MEDIA_LIMITS[kind];
  if (file.size > limit.bytes) return `${file.name} is larger than ${limit.label}.`;
  if (kind === "video" && !file.type.startsWith("video/")) return `${file.name} is not a video file.`;
  if (kind === "audio" && !file.type.startsWith("audio/")) return `${file.name} is not an audio file.`;
  if (kind === "image" && !file.type.startsWith("image/")) return `${file.name} is not an image file.`;
  const maxSeconds = "maxSeconds" in limit ? limit.maxSeconds : undefined;
  if (maxSeconds && durationSeconds && durationSeconds > maxSeconds) {
    return `Keep it under ${Math.round(maxSeconds / 60)} minutes — this one is ${formatDuration(durationSeconds)}.`;
  }
  return null;
}

/* -------------------------------- uploading -------------------------------- */

export type UploadHandle = { promise: Promise<void>; cancel: () => void };

/**
 * Uploads straight to storage over XHR so we get real progress and a working
 * cancel. Resolves only once storage confirms the write — nothing in the UI
 * claims success before that.
 */
export function uploadToStorage(
  bucket: string,
  path: string,
  file: Blob,
  accessToken: string,
  onProgress?: (percent: number) => void,
): UploadHandle {
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const xhr = new XMLHttpRequest();
  const promise = new Promise<void>((resolve, reject) => {
    xhr.open("POST", `${base}/storage/v1/object/${bucket}/${encodeURI(path)}`);
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    xhr.setRequestHeader("apikey", import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string);
    xhr.setRequestHeader("x-upsert", "false");
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(xhr.status === 413 ? "That file is too large for the server." : "The upload did not complete."));
    };
    xhr.onerror = () => reject(new Error("The connection dropped during the upload."));
    xhr.onabort = () => reject(new Error("Upload cancelled."));
    xhr.send(file);
  });
  return { promise, cancel: () => xhr.abort() };
}

export async function currentAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Your session expired — sign in again to upload.");
  return token;
}

export function mediaPath(userId: string, file: File, prefix: string) {
  const ext = (file.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "");
  const rand = Math.random().toString(36).slice(2, 10);
  return `${userId}/${prefix}/${Date.now()}-${rand}.${ext || "bin"}`;
}

/** Public URL for the photo/audio/document bucket (unchanged behaviour). */
export function publicMediaUrl(path: string) {
  return supabase.storage.from(PUBLIC_MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Marker stored in `url` for private video rows so it is never used as a src. */
export function privateMarker(path: string) {
  return `secure://${VIDEO_BUCKET}/${path}`;
}

export function isPrivateMedia(url: string | null | undefined) {
  return !!url && url.startsWith("secure://");
}

/** Which record a private video belongs to, used when resolving playback. */
export type PlaybackScope = "post" | "impact_media" | "impact_update";
